import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Result, Modal, Radio, Space, Alert } from 'antd';
import { StopOutlined, QuestionCircleOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../services/subscriptionApi';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

interface AccountSuspendedProps {
  rejectionReason?: string;
  suspendedAt?: string;
}

const AccountSuspended: React.FC<AccountSuspendedProps> = ({ 
  rejectionReason, 
  suspendedAt 
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);
  const [isPaymentFailure, setIsPaymentFailure] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);

  useEffect(() => {
    // 결제 실패로 인한 중지인지 확인
    const checkPaymentFailure = async () => {
      if (!currentUser?.id) return;

      try {
        const userId = currentUser.id || parseInt(localStorage.getItem('storeId') || '0');
        const response = await subscriptionApi.getSubscriptionByUserId(userId, {
          registrationSource: currentUser?.registrationSource ?? undefined
        });
        const sub = response.data;
        
        if (sub && (sub.status === 'suspended' || sub.status === 'payment_failed')) {
          setIsPaymentFailure(true);
          setSubscription(sub);
        }
      } catch (error: any) {
        // 구독 정보가 없거나 에러 발생 시 일반 중지로 처리
        console.error('구독 정보 조회 실패:', error);
      }
    };

    checkPaymentFailure();
  }, [currentUser]);

  const handleLogout = () => {
    console.log('계정 중지 화면에서 로그아웃 처리');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('localStorage가 정리되었습니다.');
    
    // 강제로 로그인 페이지로 이동
    console.log('window.location.replace로 강제 이동');
    window.location.replace('/login');
  };

  const handleReSubscribe = () => {
    setPlanModalVisible(true);
  };

  const handlePlanSelect = async () => {
    try {
      setLoading(true);
      // 플랜 선택 후 카드 등록 페이지로 이동
      navigate('/card-registration', { 
        state: { 
          planType: selectedPlan,
          isResubscribe: true 
        } 
      });
    } catch (error) {
      console.error('플랜 선택 실패:', error);
    } finally {
      setLoading(false);
      setPlanModalVisible(false);
    }
  };

  // 외부결제 구독은 재결제 버튼 노출 안 함(같은 영역에 안내 문구만 표시)
  const paymentSource = subscription?.paymentSource ?? subscription?.PaymentSource;
  const isExternalPayment = paymentSource === 'external';
  const showResubscribeButton = isPaymentFailure && !isExternalPayment;
  const showExternalMessage = isPaymentFailure && isExternalPayment;

  return (
    <div style={{ 
      minHeight: '100%', 
      height: '100%',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: '#1a2332'
    }}>
      <Card style={{ width: 500, textAlign: 'center' }}>
        <Result
          status="error"
          icon={<StopOutlined style={{ color: '#1a2332' }} />}
          title="계정 사용이 중지되었습니다"
          subTitle="관리자에 의해 계정이 중지되었습니다."
          extra={[
            showResubscribeButton ? (
              <Button 
                type="primary" 
                key="resubscribe" 
                icon={<CreditCardOutlined />}
                onClick={handleReSubscribe}
                style={{ 
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a',
                  color: '#ffffff'
                }}
              >
                플랜 선택 및 재결제
              </Button>
            ) : null,
            showExternalMessage ? (
              <Alert
                key="external-message"
                type="info"
                message="외부결제(크라우드펀딩 등) 고객님은 이 경로에서 재결제할 수 없습니다. 결제·갱신은 해당 플랫폼 또는 관리자에게 문의해 주세요."
                style={{ textAlign: 'left', marginBottom: 8 }}
              />
            ) : null,
            <Button 
              type="default" 
              key="logout" 
              onClick={(e) => {
                console.log('계정 중지 화면에서 버튼이 클릭되었습니다.');
                e.preventDefault();
                handleLogout();
              }}
              style={{ 
                marginLeft: (showResubscribeButton || showExternalMessage) ? 8 : 0
              }}
            >
              로그인 페이지로 돌아가기
            </Button>
          ]}
        />
        
        {rejectionReason && (
          <div style={{ 
            marginTop: 16,
            backgroundColor: 'rgba(26, 35, 50, 0.1)',
            border: '1px solid #1a2332',
            borderRadius: '16px',
            padding: 16,
            color: '#1a2332'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: '24px 1fr 24px',
              gap: 8,
              alignItems: 'flex-start'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <QuestionCircleOutlined style={{ color: '#1a2332', marginTop: 2 }} />
              </div>
              <div style={{ 
                minWidth: 0, 
                wordWrap: 'break-word', 
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>계정 중지 사유</div>
                <div style={{ 
                  fontSize: '0.8rem',
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}>
                  {rejectionReason}
                </div>
              </div>
              <div></div>
            </div>
          </div>
        )}

        {suspendedAt && (
          <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 16 }}>
            <Text type="secondary">
              중지일: {new Date(suspendedAt).toLocaleString()}
            </Text>
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          <Title level={5}>계정 중지 안내</Title>
          <div style={{ 
            backgroundColor: 'rgba(26, 35, 50, 0.05)',
            border: '1px solid rgba(26, 35, 50, 0.1)',
            borderRadius: '16px',
            padding: 16,
            textAlign: 'left'
          }}>
            {isPaymentFailure ? (
              <Text style={{ color: '#1a2332', fontSize: '0.9rem' }}>
                • 결제 실패로 인해 계정이 중지되었습니다.<br />
                • 플랜을 선택하고 결제를 진행하시면 서비스를 다시 이용하실 수 있습니다.<br />
                • 위의 "플랜 선택 및 재결제" 버튼을 클릭하여 결제를 진행해주세요.
              </Text>
            ) : (
              <Text style={{ color: '#1a2332', fontSize: '0.9rem' }}>
                • 계정 중지와 관련하여 담당자가 연락드릴 예정입니다.<br />
                • 계정 중지 해제는 관리자 승인 후 가능합니다.<br />
                • 중지 해제 후 다시 로그인하여 서비스를 이용하실 수 있습니다.
              </Text>
            )}
          </div>
        </div>
      </Card>

      {/* 플랜 선택 모달 */}
      <Modal
        title="플랜 선택 및 재결제"
        open={planModalVisible}
        onOk={handlePlanSelect}
        onCancel={() => setPlanModalVisible(false)}
        okText="결제 진행"
        cancelText="취소"
        confirmLoading={loading}
      >
        <Alert
          message="결제 실패로 인해 계정이 중지되었습니다"
          description="플랜을 선택하고 결제를 진행하시면 서비스를 다시 이용하실 수 있습니다."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Radio.Group value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Radio value="monthly">
              <div>
                <Title level={5} style={{ margin: 0 }}>월간 플랜</Title>
                <Text>월 9,900원</Text>
              </div>
            </Radio>
            <Radio value="yearly">
              <div>
                <Title level={5} style={{ margin: 0 }}>연간 플랜</Title>
                <Text>월 7,900원 (연 94,800원) - 연간 플랜 선택 시 월간 대비 20% 할인</Text>
              </div>
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </div>
  );
};

export default AccountSuspended; 